import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/sgokit.svg';
import { NAV } from './data';
import { Arrow } from './icons';

/**
 * Shell for every SGOKit platform (marketing) page. Everything renders inside
 * `.sgk-site`, which scopes sgokit.css so the OCEN tenant demo is untouched.
 */
export default function PlatformLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Scroll-in reveals. Cheap IntersectionObserver over anything tagged
  // .sgk-reveal; reduced-motion users get everything shown immediately (CSS).
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.sgk-reveal');
    if (!els?.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-in')),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);

  useEffect(() => { window.scrollTo(0, 0); setMenuOpen(false); }, [pathname]);

  return (
    <div className="sgk-site" ref={rootRef}>
      <header className="sgk-header">
        <div className="sgk-wrap sgk-nav">
          <Link to="/" className="sgk-brand" data-testid="platform-brand-link">
            <img className="sgk-brand__logo" src={logo} alt="" />
            <span className="sgk-brand__name">SGO<b>Kit</b></span>
          </Link>

          <nav className="sgk-nav__links">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to}
                className={pathname.startsWith(n.to) ? 'is-active' : ''}
                data-testid={`platform-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}-link`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="sgk-nav__cta">
            <Link to="/login" className="sgk-btn sgk-btn--ghost" data-testid="platform-signin-link">Sign in</Link>
            <Link to="/signup" className="sgk-btn sgk-btn--gold" data-testid="platform-start-link">
              Start an SGO <Arrow />
            </Link>
            <button className="sgk-btn sgk-btn--ghost sgk-nav__toggle" onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen} aria-label="Menu" data-testid="platform-menu-toggle-button">☰</button>
          </div>
        </div>

        {menuOpen && (
          <div className="sgk-wrap" style={{ paddingBottom: 16 }} data-testid="platform-mobile-menu">
            <nav style={{ display: 'grid', gap: 4 }}>
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} style={{ padding: '8px 0', fontWeight: 600 }}
                  data-testid={`platform-mobilenav-${n.label.toLowerCase().replace(/\s+/g, '-')}-link`}>
                  {n.label}
                </Link>
              ))}
              <Link to="/login" style={{ padding: '8px 0', fontWeight: 600 }} data-testid="platform-mobile-signin-link">Sign in</Link>
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="sgk-footer">
        <div className="sgk-wrap">
          <div className="sgk-footer__grid">
            <div className="sgk-footer__brand">
              <div className="sgk-brand">
                <img className="sgk-brand__logo" src={logo} alt="" />
                <span className="sgk-brand__name">SGO<b>Kit</b></span>
              </div>
              <p className="sgk-footer__tag">Your toolbox for education empowerment. Helping you rebuild the laboratory of democracy — one scholarship at a time.</p>
            </div>
            <div>
              <h4>Platform</h4>
              <ul>
                <li><Link to="/platform" data-testid="footer-platform-link">The toolkit</Link></li>
                <li><Link to="/footprint" data-testid="footer-footprint-link">State footprint</Link></li>
                <li><Link to="/site" data-testid="footer-site-link">Your site</Link></li>
                <li><Link to="/pricing" data-testid="footer-pricing-link">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><Link to="/about" data-testid="footer-about-link">Mission</Link></li>
                <li><Link to="/signup" data-testid="footer-start-link">Start an SGO</Link></li>
                <li><Link to="/login" data-testid="footer-signin-link">Sign in</Link></li>
              </ul>
            </div>
            <div>
              <h4>The credit</h4>
              <ul>
                <li><Link to="/footprint" data-testid="footer-s25f-link">Section 25F &amp; OBBBA</Link></li>
                <li><Link to="/footprint" data-testid="footer-states-link">Where it&apos;s live</Link></li>
                <li><Link to="/about" data-testid="footer-eligibility-link">Who qualifies</Link></li>
              </ul>
            </div>
          </div>
          <div className="sgk-footer__legal">
            <span>© {new Date().getFullYear()} SGOKit. Not tax or legal advice.</span>
            <span className="sgk-num">Federal credit up to $1,700 · begins 2027 · states must opt in</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
